import { type VariantProps, cva } from 'class-variance-authority';

const badge = cva(
  'inline-flex rounded-circle justify-center items-center text-label text-content-inverse',
  {
    variants: {
      intent: {
        filter: 'bg-content-info',
        notification: 'bg-content-negative',
        messages: 'bg-messages-badge-background text-messages-badge-foreground',
        attention: 'bg-attention !text-content-warning',
      },
      size: {
        extraSmall: 'w-[8px] h-[8px]',
        small: 'w-[12px] h-[12px]',
        medium: 'w-[16px] h-[16px]',
        large: 'w-[20px] h-[20px]',
        extraLarge: 'w-[32px] h-[20px]',
      },
      pulse: {
        true: 'animate-count-pulse motion-reduce:animate-none',
      },
    },
    defaultVariants: {
      intent: 'notification',
      size: 'medium',
    },
  },
);

type ConditionalBadgeProps =
  | { value?: number; size?: 'medium' | 'large' | 'extraLarge' }
  | { value?: never; size?: 'extraSmall' | 'small' };
type BadgeProps = ConditionalBadgeProps & VariantProps<typeof badge>;

/**
 * A badge is a small circle that can be used to indicate a count or a status.
 * Only medium, large and extraLarge sizes can accommodate numbers.
 */
export const Badge = ({ intent, value, size, pulse }: BadgeProps) => {
  if (!value) {
    return <div className={badge({ intent, size, pulse })} />;
  }

  const isLargeValue = value && value > 9;
  const newSize = isLargeValue ? 'extraLarge' : size;
  const displayValue = isLargeValue ? '9+' : value?.toString();

  return (
    <div className={badge({ intent, size: newSize, pulse })}>
      {displayValue}
    </div>
  );
};
